import React, {useState, useEffect} from 'react'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import Checkbox from '#components/a/form/Checkbox'
import IconButton from '#components/a/IconButton'
import Headline from '#components/a/Headline'
import Modal from '#components/m/Modal'
import Search from '#components/m/Search/index'
import DropdownSelect from '#components/m/DropdownSelect'
import {filterTypes} from './WissensportalConstants'

function WissensportalFilterModal({trigger, allCourses, allModules, confirmCallback, parentFilters}) {
  const WORK_STATUSES = [
    {value: 'NOT_STARTED', label: `${i18n.t('wissensportal_status_NOT_STARTED')}`, type: filterTypes.status},
    {value: 'IN_PROGRESS', label: `${i18n.t('wissensportal_status_IN_PROGRESS')}`, type: filterTypes.status},
    {value: 'FINISHED', label: `${i18n.t('wissensportal_status_FINISHED')}`, type: filterTypes.status},
  ]
  const [selectedFilters, setFilters] = React.useState(parentFilters)
  const [showModulesFromSelectedCoursesOnly, setModulesFromSelectedCoursesOnly] = useState(false)
  const [moduleOptions, setModuleOptions] = useState()
  const [courseOptions, setCourseOptions] = useState()
  const [namesOfCourses, setNamesOfCourses] = useState([])
  const [namesOfModules, setNamesOfModules] = useState([])

  useEffect(() => {
    setFilters(parentFilters)
  }, [parentFilters])

  useEffect(() => {
    setNamesOfCourses(allCourses.map(course => `${i18n.t('wissensportal_course')}: ${course.name}`))
    setNamesOfModules(allModules.map(module => `${i18n.t('wissensportal_module')}: ${module.name}`))
    setCourseOptions(allCourses.map(x => ({id: x.id, label: x.name, type: x.type})))
  }, [allCourses, allModules])

  const listOfAllCoursesModules = namesOfCourses.concat(namesOfModules)

  useEffect(() => {
    if (showModulesFromSelectedCoursesOnly) {
      const selectedCourses = allCourses.filter(c => selectedFilters.courses.some(sc => sc.id === c.id))
      setModuleOptions(renderModuleOptions(selectedCourses))
    } else {
      setModuleOptions(renderModuleOptions(allCourses))
    }
  }, [showModulesFromSelectedCoursesOnly, allCourses, selectedFilters.courses])

  const renderModuleOptions = courses =>
    courses.map(course => ({
      label: course.name,
      options: course.modules.map(m => ({
        label: m.name,
        name: m.name,
        status: m.status,
        id: m.id,
        type: m.type,
        courseId: course.id,
      })),
    }))

  const renderFilters = list => (
    <div className="list-container">
      {list.map(item => (
        <div key={item.id.toString()} className="filter-container">
          <span className="filter">{item.label}</span>
          <IconButton event={() => removeFilter(item)} className="filter_delete" icon="close" />
        </div>
      ))}
    </div>
  )

  const addFilter = item => {
    if (item.type === filterTypes.course) {
      if (selectedFilters.courses.findIndex(i => i.id === item.id) === -1) {
        const newSelectedCourses = selectedFilters.courses ? [...selectedFilters.courses, item] : [item]
        setFilters({...selectedFilters, courses: newSelectedCourses})
      }
    } else if (item.type === filterTypes.course_module || item.type === filterTypes.folder) {
      if (selectedFilters.modules.findIndex(i => i.id === item.id) === -1) {
        const newSelectedModules = selectedFilters.modules ? [...selectedFilters.modules, item] : [item]
        setFilters({...selectedFilters, modules: newSelectedModules})
      }
    } else if (item.type === filterTypes.status) {
      setFilters({...selectedFilters, status: item.value})
    }
  }

  const addFilterBySearchText = text => {
    const toBeCutText = text.split(' ')[0]
    const filterType = toBeCutText.slice(0, -1)
    const name = text.replace(toBeCutText, '').trim()
    if (filterType === i18n.t('wissensportal_course')) {
      const filter = courseOptions.find(f => f.label === name)
      addFilter(filter)
    } else if (filterType === i18n.t('wissensportal_module')) {
      let filter
      moduleOptions.forEach(course => {
        if (filter === undefined) {
          filter = course.options.find(module => module.label === name)
        }
      })
      addFilter(filter)
    }
  }

  const removeFilter = item => {
    if (item.type === filterTypes.course) {
      const newSelectedCourses = selectedFilters.courses.filter(course => item.id !== course.id)
      setFilters({...selectedFilters, courses: newSelectedCourses})
    } else if (item.type === filterTypes.course_module || item.type === filterTypes.folder) {
      const newSelectedModules = selectedFilters.modules.filter(module => item.id !== module.id)
      setFilters({...selectedFilters, modules: newSelectedModules})
    }
  }

  const handleModulesFromSelectedCoursesOnly = () => {
    setModulesFromSelectedCoursesOnly(prev => !prev)
  }

  return (
    <div className="wissensportal_modal">
      <Modal
        id={Math.random().toString()}
        modifier="wissensportal"
        trigger={trigger}
        confirmCallback={() => confirmCallback(selectedFilters)}
        confirmText={i18n.t('modal_button_apply')}
        hasCloseButton>
        <div data-testid="wissensportal_modal">
          <Headline modifier="article" importance={2} text={i18n.t('wissensportal_show_filter')} />
          <Search
            type="wissensportal"
            list={listOfAllCoursesModules}
            clickHandler={text => addFilterBySearchText(text)}
          />
          <DropdownSelect
            options={WORK_STATUSES}
            selectedOption={{label: i18n.t(`wissensportal_status_${selectedFilters.status}`)}}
            handleSelect={e => addFilter(e)}
          />
          {selectedFilters.status === '' && <div className="hint">{i18n.t('wissensportal_no_selection')}</div>}
          <DropdownSelect
            options={courseOptions}
            selectedOption={{label: i18n.t('wissensportal_courses')}}
            handleSelect={e => addFilter(e)}
          />
          {selectedFilters.courses.length > 0 ? (
            renderFilters(selectedFilters.courses)
          ) : (
            <div className="hint">{i18n.t('wissensportal_no_selection')}</div>
          )}
          <div className="filterOption">
            <Checkbox
              onChangeHandle={() => handleModulesFromSelectedCoursesOnly()}
              checked={showModulesFromSelectedCoursesOnly}
            />
            {i18n.t('wissensportal_show_modules_from_selected_courses')}
          </div>
          <DropdownSelect
            options={moduleOptions}
            selectedOption={{label: i18n.t('wissensportal_modules')}}
            handleSelect={e => {
              addFilter(e)
            }}
          />
          {selectedFilters.modules.length > 0 ? (
            renderFilters(selectedFilters.modules)
          ) : (
            <div className="hint">{i18n.t('wissensportal_no_selection')}</div>
          )}
        </div>
      </Modal>
    </div>
  )
}

WissensportalFilterModal.propTypes = {
  trigger: PropTypes.node,
  allCourses: PropTypes.array,
  allModules: PropTypes.array,
  confirmCallback: PropTypes.func,
  parentFilters: PropTypes.object,
}

export default WissensportalFilterModal
